import React, { useState } from 'react';
import './QASessionPreview.css';
import MessageRenderer from './MessageRenderer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';

interface QACard {
  id?: string;
  question: string;
  answer: string;
  source?: string;
  feedback?: 'thumbs_up' | 'thumbs_down' | 'need_review';
}

interface QASessionPreviewProps {
  qaCards: QACard[];
  messageId: string;
  onQAFeedback?: (messageId: string, cardIndex: number, type: 'thumbs_up' | 'thumbs_down' | 'need_review', reason?: string) => void;
  theme?: 'light' | 'dark';
  feedbackOptions?: Array<{ value: string; label: string }>;
  showFeedbackReasonFor?: string | null;
  onShowFeedbackReason?: (id: string | null) => void;
  botName?: string;
}

const QASessionPreview: React.FC<QASessionPreviewProps> = ({ 
  qaCards, 
  messageId,
  onQAFeedback,
  theme = 'light',
  feedbackOptions = [],
  showFeedbackReasonFor,
  onShowFeedbackReason,
  botName
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Get preview content (first 2 Q&As)
  const previewCards = qaCards.slice(0, 2);

  if (isExpanded) {
    // Full conversation view
    return (
      <div className={`qa-session-expanded ${theme}`}>
        <div className="qa-session-header">
          <h3>Q&A Session ({qaCards.length} questions)</h3>
          <button onClick={toggleExpand} className="qa-close-btn">
            Collapse ✕
          </button>
        </div>
        <div className="space-y-6">
          {qaCards.map((card, index) => (
            <div key={card.id || `qa-${index}`} className="space-y-4">
              {/* User question - styled like demo */}
              <div className="mb-6">
                <div className="flex justify-end">
                  <div className="max-w-[85%] order-1">
                    <div className={`rounded-2xl px-5 py-3 ${
                      theme === 'dark' 
                        ? 'bg-gray-700 text-gray-100' 
                        : 'bg-gray-100 text-gray-900'
                    }`}>
                      <div className="whitespace-pre-wrap">{card.question}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Assistant answer - styled like demo */}
              <div className="mb-6">
                <div className="flex justify-start">
                  <div className="w-full">
                    <div className="flex items-start gap-3 mb-1">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        theme === 'dark' ? 'bg-blue-600' : 'bg-blue-500'
                      } text-white text-sm font-bold`}>
                        {botName ? botName.substring(0, 2).toUpperCase() : 'AI'}
                      </div>
                      <span className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>{botName || 'AI Assistant'}</span>
                    </div>
                    <div className={`rounded-2xl px-5 py-3 ${
                      theme === 'dark'
                        ? 'bg-transparent text-gray-100'
                        : 'bg-transparent text-gray-900'
                    }`}>
                      <div className={`message-response-display ${theme === 'dark' ? 'dark-theme' : ''}`}>
                        <MessageRenderer 
                          content={card.answer} 
                          messageId={`qa-${messageId}-${index}`}
                          className={theme === 'dark' ? 'dark-theme' : ''}
                        />
                      </div>
                      {card.source && (
                        <div className={`flex items-center text-xs mt-2 ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          <FontAwesomeIcon icon={faGlobe} className="mr-1" />
                          Source: {card.source}
                        </div>
                      )}
                    </div>
                    
                    {/* Feedback buttons */}
                    {onQAFeedback && (
                      <>
                    <div className="feedback-buttons qa-feedback">
                      <button
                        className={`feedback-btn thumbs-up ${card.feedback === 'thumbs_up' ? 'active' : ''}`}
                        onClick={() => onQAFeedback(messageId, index, 'thumbs_up')}
                        disabled={!!card.feedback}
                      >
                        👍
                      </button>
                      <button
                        className={`feedback-btn thumbs-down ${card.feedback === 'thumbs_down' ? 'active' : ''}`}
                        onClick={() => onQAFeedback(messageId, index, 'thumbs_down')}
                        disabled={!!card.feedback}
                      >
                        👎
                      </button>
                      <button
                        className={`feedback-btn need-review ${card.feedback === 'need_review' ? 'active' : ''}`}
                        onClick={() => onQAFeedback(messageId, index, 'need_review')}
                        disabled={!!card.feedback}
                      >
                        ⚠️
                      </button>
                    </div>
                    
                    {/* Feedback reasons */}
                    {showFeedbackReasonFor === `qa-${messageId}-${index}` && feedbackOptions.length > 0 && (
                      <div className="feedback-reason-tags">
                        <span className="feedback-tags-label">What went wrong?</span>
                        <div className="feedback-tags-container">
                          {feedbackOptions.map((option) => (
                            <button
                              key={option.value}
                              className="feedback-tag"
                              onClick={() => {
                                onQAFeedback(messageId, index, 'thumbs_down', option.value);
                                if (onShowFeedbackReason) {
                                  onShowFeedbackReason(null);
                                }
                              }}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Compact preview
  return (
    <div className={`qa-session-preview ${theme}`} onClick={toggleExpand}>
      <div className="qa-preview-content">
        {previewCards.map((card, index) => (
          <div key={card.id || `preview-${index}`} className="qa-preview-item">
            <p className="qa-preview-q">Q: {card.question.slice(0, 50)}...</p>
            <p className="qa-preview-a">A: {card.answer.slice(0, 60)}...</p>
          </div>
        ))}
        <div className="qa-preview-fade"></div>
      </div>
      <div className="qa-preview-footer">
        <span className="qa-label">Q&A SESSION</span>
        <span className="qa-count">{qaCards.length}</span>
      </div>
    </div>
  );
};

export default QASessionPreview;